import PricingModal from "$/components/PricingModal";
import commonConstants from "$/data/common";
import { useAuth } from "$/util/auth";
import { getCookie } from "$/util/cookies";
import { updateIpInfo } from "$/util/db";
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import CreateRouter from "../components/CreateRouter";
import HeroSection from "../components/HeroSection";
import HomeWalkthroughSection from "../components/HomeWalkthroughSection";
import Meta from "../components/Meta";

function IndexPage() {
  const navigate = useNavigate();

  React.useEffect(() => {
    const url = window.location.href;
    const params = url.substring(url.lastIndexOf("/") + 1);
    const parsedGetParams = params.replace("?", "");
    const isLoggedIn = new URL(location.href).searchParams.get("login");
    if (isLoggedIn) {
      let provider = new URL(location.href).searchParams.get("provider");
      if (!provider) {
        provider = "magiclink";
      } else {
        provider = provider + "_auth";
      }
      const getUtmBucket = localStorage.getItem("__UTM_BUCKET");
      if (getUtmBucket) {
        const getUtmBucketParsed = JSON.parse(getUtmBucket);
        localStorage.removeItem("__UTM_BUCKET");
        navigate(
          window.location.search +
            "&" +
            getUtmBucketParsed +
            "&" +
            "utm_content=" +
            provider,
          {
            replace: true,
          },
        );
      }
    }

    if (parsedGetParams.length > 0) {
      // //https://blackink.ai/?login=true&provider=google#
      if (parsedGetParams.includes("utm")) {
        localStorage.setItem("__UTM_BUCKET", JSON.stringify(parsedGetParams));
      }
    }
  }, [window]);

  React.useEffect(() => {
    //remove the pro_yearly/pro_monthly url params(after 3 seconds) to prevent double tracking issue in GA.
    let analyticsTimerId: ReturnType<typeof setTimeout> | undefined;
    const url = window.location.href;
    const params = url.substring(url.lastIndexOf("/") + 1);
    if (
      params.includes("pro_yearly=true") ||
      params.includes("pro_quarterly=true") ||
      params.includes("pro_monthly=true")
    ) {
      analyticsTimerId = setTimeout(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete("pro_yearly");
        url.searchParams.delete("pro_quarterly");
        url.searchParams.delete("pro_monthly");
        window.history.pushState(null, "", url.toString());
      }, 3000);
    }
    return () => clearTimeout(analyticsTimerId);
  }, [window]);

  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const [ipInfoUpdate, setIpInfoUpdate] = React.useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);

  React.useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | undefined;
    if (auth.user) {
      if (
        commonConstants.FREE_CREDITS === auth.user.credits &&
        !auth.user.isActiveSubscription
      ) {
        const nextURL = getCookie("next_url");
        if (nextURL) {
          timerId = setTimeout(() => {
            setShowPricingModal(true);
          }, 1500);
        }
      }
    }
    return () => clearTimeout(timerId);
  }, [auth]);

  React.useEffect(() => {
    let timeoutId: string | number | NodeJS.Timeout;
    if (auth.user) {
      if (searchParams.get("login") !== null && !ipInfoUpdate) {
        // check if city or country is already updated if not update
        if (
          [null, "", undefined].includes(auth.user.city) ||
          [null, "", undefined].includes(auth.user.country)
        ) {
          // todo fix with better solution
          // get IP of the current user using webrtc-ip
          // get IP block info
          // insert country and city
          const script = document.createElement("script");
          script.type = "text/javascript";
          script.src =
            "https://cdn.jsdelivr.net/gh/joeymalvinni/webrtc-ip/dist/bundle.dev.js";
          document.head.appendChild(script);
          try {
            timeoutId = window.setTimeout(() => {
              // todo fix type for getIPs()
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              getIPs()
                .then(async (res: string[]) => {
                  if (res.length > 0) {
                    const ipToSearch = res[0].length <= 15 ? res[0] : res[1];
                    if (ipToSearch) {
                      fetch(
                        `https://ipinfo.io/${ipToSearch}${
                          import.meta.env.VITE_APP_IPINFO_API_KEY
                            ? `?token=${import.meta.env.VITE_APP_IPINFO_API_KEY}`
                            : "/json"
                        }`,
                      )
                        .then((response) => {
                          return response.json();
                        })
                        .then(async (response) => {
                          const city = response.city;
                          const country = response.country;
                          await updateIpInfo(auth?.user, country, city);
                          setIpInfoUpdate(true);
                        })
                        .catch((error) => {
                          console.debug(error);
                          setIpInfoUpdate(true);
                        });
                    }
                  }
                })
                .catch((err) => {
                  console.debug(err);
                });
            }, 1900);
          } catch (e) {
            console.debug("e", e);
          }
        }
      }
    }

    return () => clearTimeout(timeoutId);
  }, [auth, ipInfoUpdate]);

  return (
    <>
      <Meta />
      <Helmet>
        {/* Schema Tags */}
        <script type="application/ld+json" key="json-ld">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            url: "https://blackink.ai/",
            sameAs: [
              "https://www.instagram.com/blackink_ai/",
              "https://twitter.com/BlackInkAI",
              "https://www.facebook.com/BlackInkAI",
            ],
            logo: "https://blackink.ai/images/logo_invert.svg",
            name: "BlackInk.AI",
            alternateName: "BlackInk AI",
            legalName: "BlackInk AI, LLC",
            foundingDate: "2021-11-01",
            description:
              "Design custom, unique tattoos in seconds with the BlackInk AI tattoo generator. Discover millions of AI-generated tattoo designs.",
            email: "info@blackink.ai",
          })}
        </script>

        {/* Schema Tags */}
        <script type="application/ld+json" key="json-ld">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "BlackInk",
            url: "https://blackink.ai/",
            "@id": "https://blackink.ai/#webapplication",
            browserRequirements: ["requires HTML5 support", "requires JavaScript"],
            sameAs: "https://blackink.ai/",
            applicationCategory: "TattooDesignerApplication",
            permissions: "may run only with an active internet connection",
            operatingSystem: [
              "Windows 8.1 or later",
              "macOS 10.13 or later",
              "Chromebook",
            ],
            featureList: [
              "https://blackink.ai/tattoo-design-generator",
              "https://blackink.ai/tattoo-ideas-generator",
              "https://blackink.ai/tattoo-stencil-maker",
            ],
            copyrightHolder: {
              "@type": "Organization",
              name: "BlackInk.AI",
              legalName: "BlackInk AI, LLC",
              "@id": "https://blackink.ai/#organization",
              sameAs: [
                "https://www.instagram.com/blackink_ai/",
                "https://twitter.com/BlackInkAI",
                "https://www.facebook.com/BlackInkAI",
              ],
              logo: "https://blackink.ai/images/logo_invert.svg",
              foundingDate: "2021-11-01",
              description:
                "Design custom, unique tattoos in seconds with the BlackInk AI tattoo generator. Discover millions of AI-generated tattoo designs.",
              email: "info@blackink.ai",
            },
            creator: {
              "@type": "Organization",
              "@id": "https://blackink.ai/#organization",
              name: "BlackInk.AI",
              url: "https://blackink.ai/",
            },
            publisher: {
              "@type": "Organization",
              "@id": "https://blackink.ai/#organization",
              name: "BlackInk.AI",
              url: "https://blackink.ai/",
            },
            offers: {
              "@type": "AggregateOffer",
              highPrice: "15.00",
              lowPrice: "6.00",
              priceCurrency: "USD",
              offerCount: "2",
              offers: [
                {
                  "@type": "Offer",
                  price: "15.00",
                  priceCurrency: "USD",
                  name: "BlackInk AI monthly subscription",
                  url: "https://blackink.ai/pricing",
                },
                {
                  "@type": "Offer",
                  price: "6.00",
                  priceCurrency: "USD",
                  name: "BlackInk AI Pro yearly subscription",
                  url: "https://blackink.ai/pricing",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      {showPricingModal && (
        <PricingModal
          modalClosedCallback={() => setShowPricingModal(false)}
          forceOpen
        />
      )}
      <CreateRouter />
      <HeroSection />
      <HomeWalkthroughSection />
    </>
  );
}

export default IndexPage;
